import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "../lib/api";

export default function HomeSettings() {
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  if (!settings) return <p className="text-sm">Chargement…</p>;

  const update = (k, v) => setSettings({ ...settings, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    try {
      await api.put("/settings", settings);
      setMsg("Page d'accueil mise à jour ✓");
    } catch (e2) {
      setMsg("Erreur : " + (e2.response?.data?.detail || e2.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid="home-settings-form"
      className="brutal-card p-6 lg:p-8 max-w-3xl flex flex-col gap-5"
      style={{ background: "#D9C5FD" }}
    >
      <h2 className="font-display text-3xl">Page d&apos;accueil</h2>
      <p className="text-sm text-black/70 -mt-3">
        Modifie le grand titre, le bandeau, et les phrases qui s&apos;affichent en home.
      </p>

      <Section title="Hero (en haut de la home)">
        <Field
          label="Petit bandeau (kicker) — laisse vide pour le cacher"
          testid="set-hero-kicker"
          value={settings.hero_kicker}
          onChange={(v) => update("hero_kicker", v)}
          placeholder="ex: Numéro 04 · Hiver 26"
        />
        <Field
          label="Grand titre — ligne 1"
          testid="set-hero-1"
          value={settings.hero_title_1}
          onChange={(v) => update("hero_title_1", v)}
        />
        <Field
          label="Grand titre — ligne 2 (le premier mot sera coloré en vert)"
          testid="set-hero-2"
          value={settings.hero_title_2}
          onChange={(v) => update("hero_title_2", v)}
        />
      </Section>

      <Section title="Confession Intime">
        <Field
          label="Titre — ligne 1"
          testid="set-conf-1"
          value={settings.confession_title_1}
          onChange={(v) => update("confession_title_1", v)}
        />
        <Field
          label="Titre — ligne 2 (jaune)"
          testid="set-conf-2"
          value={settings.confession_title_2}
          onChange={(v) => update("confession_title_2", v)}
        />
        <Textarea
          label="Texte d'intro de la boîte de confession"
          testid="set-conf-intro"
          value={settings.confession_intro}
          onChange={(v) => update("confession_intro", v)}
        />
        <Field
          label="Pseudo Instagram"
          testid="set-insta"
          value={settings.instagram_handle}
          onChange={(v) => update("instagram_handle", v)}
        />
      </Section>

      <Section title="Section Podcast (sur la home)">
        <Field
          label="Petit bandeau (kicker)"
          testid="set-pod-kicker"
          value={settings.podcast_kicker}
          onChange={(v) => update("podcast_kicker", v)}
          placeholder="ex: Podcast · tous les vendredis"
        />
        <Field
          label="Titre — ligne 1 (le ! sera coloré)"
          testid="set-pod-1"
          value={settings.podcast_title_1}
          onChange={(v) => update("podcast_title_1", v)}
        />
        <Field
          label="Titre — ligne 2"
          testid="set-pod-2"
          value={settings.podcast_title_2}
          onChange={(v) => update("podcast_title_2", v)}
        />
        <Textarea
          label="Texte d'intro du podcast"
          testid="set-pod-intro"
          value={settings.podcast_intro}
          onChange={(v) => update("podcast_intro", v)}
        />
      </Section>

      <Section title="Manifeste (bas de page noir)">
        <Field
          label="Petit titre au-dessus"
          testid="set-manif-kicker"
          value={settings.manifesto_kicker}
          onChange={(v) => update("manifesto_kicker", v)}
        />
        <Textarea
          label="Grande phrase manifeste"
          testid="set-manif-text"
          value={settings.manifesto_text}
          onChange={(v) => update("manifesto_text", v)}
        />
      </Section>

      <Section title="Analytics (Google Analytics 4)">
        <Field
          label="ID de mesure GA4 (laisser vide pour désactiver le tracking)"
          testid="set-ga-id"
          value={settings.ga_measurement_id}
          onChange={(v) => update("ga_measurement_id", v)}
          placeholder="G-XXXXXXXXXX"
        />
        <p className="text-xs text-black/60 -mt-1">
          Le tracking ne s&apos;active qu&apos;après acceptation des cookies par le visiteur (RGPD).
        </p>
      </Section>

      {msg && <p data-testid="settings-msg" className="text-sm font-bold">{msg}</p>}
      <button
        data-testid="settings-submit"
        type="submit"
        disabled={loading}
        className="brutal-btn brutal-btn-coral self-start"
      >
        <Save size={16} /> {loading ? "Enregistrement…" : "Enregistrer"}
      </button>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="border-2 border-black rounded-2xl p-4 flex flex-col gap-3" style={{ background: "#FFFDF7" }}>
      <legend className="px-2 text-xs uppercase font-bold tracking-widest">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase font-bold tracking-widest">{label}</span>
      <input
        data-testid={testid}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase font-bold tracking-widest">{label}</span>
      <textarea
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none min-h-[90px]"
      />
    </label>
  );
}
